import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { prettify } from '../../helpers/prices';

const KEY_CODE_ESC = 'Escape';

interface NavItemProps {
  text: string;
  icon: string;
  onClick: () => void;
  current: boolean;
}

const NavItem = ({ text, onClick, current, icon }: NavItemProps) => (
  <li className={`lightbox-nav-item${current ? ' is-current' : ''}`}>
    <button type="button" className="lightbox-nav-action" onClick={onClick}>
      <i className={`m-r-half ${icon}`} />
      {text}
    </button>
  </li>
);

const embedAndVideoVisible = ({ embedAndVideo }: { embedAndVideo: number }, index: number) =>
  embedAndVideo >= 0 ? embedAndVideo <= index : false;

const threesixtyAndSphereVisible = (
  { threesixtyAndSphere, embedAndVideo }: { threesixtyAndSphere: number; embedAndVideo: number },
  index: number,
) =>
  threesixtyAndSphere >= 0
    ? !embedAndVideoVisible({ embedAndVideo }, index) && threesixtyAndSphere <= index
    : false;

const imageVisible = ({ image, threesixtyAndSphere, embedAndVideo }: ControlMenu, index: number) =>
  image >= 0
    ? !threesixtyAndSphereVisible({ threesixtyAndSphere, embedAndVideo }, index) &&
      !embedAndVideoVisible({ embedAndVideo }, index) &&
      image <= index
    : false;

interface ControlMenu {
  image: number;
  threesixtyAndSphere: number;
  embedAndVideo: number;
}

interface LightboxDataUpsItem {
  icon: string;
  value: string | number;
}

interface LightboxData {
  price: number | string;
  privateLeasing: number;
  businessLeasing: number;
  usps: LightboxDataUpsItem[];
}

interface LightboxProps {
  className?: string;
  data: LightboxData | null;
  controlMenu: ControlMenu;
  startIndex?: number;
  children: React.ReactNode | React.ReactNode[];
  modifiers?: string;
  isOrder?: boolean;
  onClose: () => void;
}

const Lightbox = ({
  startIndex,
  modifiers = '',
  className,
  controlMenu,
  data,
  children,
  isOrder,
  onClose,
}: LightboxProps) => {
  const { t } = useTranslation(['common']);
  const [visibleIndex, setVisibleIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown as unknown as (e: Event) => void);

    if (startIndex !== undefined && startIndex > -1) {
      const node = document.querySelector<HTMLElement>(`#lightbox-item-${startIndex}`);
      const containerMobile = document.querySelector('.lightbox-media-container');
      const containerDesktop = document.querySelector('[data-am-lightbox]');

      if (node && containerMobile && containerDesktop) {
        containerMobile.scrollTop = containerMobile.scrollTop = node.offsetTop;
        containerDesktop.scrollTop = containerDesktop.scrollTop = node.offsetTop;
      }
    }

    return () => {
      window.removeEventListener('keydown', onKeyDown as unknown as (e: Event) => void);
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === KEY_CODE_ESC) {
      onClose();
    }
  };

  const indexIsVisible = (index: number) => {
    const node = document.querySelector(`#lightbox-item-${index}`);

    if (node && ref.current) {
      const bounds = node.getBoundingClientRect();
      const inViewport =
        bounds.top >= -1 * (bounds.height / 2) &&
        bounds.left >= -1 * (bounds.width / 2) &&
        bounds.bottom <= ref.current.clientHeight + bounds.height / 2 &&
        bounds.right <= ref.current.clientWidth;

      return inViewport;
    }
  };

  const onPageScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (
      e &&
      e.currentTarget &&
      e.currentTarget.scrollTop === e.currentTarget.scrollHeight - e.currentTarget.offsetHeight
    ) {
      setVisibleIndex(Array.isArray(children) ? children?.length || 0 : 0);
    } else {
      for (let i = 0; i < (Array.isArray(children) ? children?.length : 0); i++) {
        if (indexIsVisible(i)) {
          setVisibleIndex(i);
          break;
        }
      }
    }
  };

  const scrollInto = (index: number) => {
    const node = document.querySelector<HTMLDivElement>(`#lightbox-item-${index}`);
    const containerMobile = document.querySelector('.lightbox-media-container');
    const containerDesktop = document.querySelector('[data-am-lightbox]');

    if (node && containerMobile && containerDesktop) {
      containerMobile.scrollTop = containerMobile.scrollTop = node.offsetTop - 70;
      containerDesktop.scrollTop = containerDesktop.scrollTop = node.offsetTop;
    }
  };

  let price = null;
  let privateLeasing = null;
  let businessLeasing = null;

  if (data) {
    if (data.price) {
      price = (
        <div className="m-b-half">
          <div className="text-dark-lighten font-size-small">
            {isOrder
              ? t('priceLabel.recommendedRetailPrice', {
                  ns: 'common',
                })
              : t('priceLabel.cash', { ns: 'common' })}
          </div>
          <div className="font-medium">
            {typeof data.price === 'number' ? prettify(data.price) : data.price}
          </div>
        </div>
      );
    }
    if (data.privateLeasing) {
      privateLeasing = (
        <div className="m-b-half">
          <div className="text-dark-lighten font-size-small">
            {t('priceLabel.privateLeasing', { ns: 'common' })}
          </div>
          <div className="font-medium">
            {t('Price per month', {
              ns: 'common',
              count: data.privateLeasing,
            })}
          </div>
        </div>
      );
    }
    if (data.businessLeasing) {
      businessLeasing = (
        <div className="m-b-half">
          <div className="text-dark-lighten font-size-small">
            {t('priceLabel.businessLeasing', { ns: 'common' })}
          </div>
          <div className="font-medium">
            {t('Price per month', {
              ns: 'common',
              count: data.businessLeasing,
            })}
          </div>
        </div>
      );
    }
  }

  let menu;
  if (controlMenu) {
    menu = (
      <nav className="lightbox-nav">
        <ul className="lightbox-nav-list" data-am-list="">
          {controlMenu.image >= 0 && (
            <NavItem
              text={t('mediaImages', { ns: 'common' })}
              icon="icon-image"
              onClick={() => scrollInto(controlMenu.image)}
              current={imageVisible(controlMenu, visibleIndex)}
            />
          )}
          {controlMenu.threesixtyAndSphere >= 0 && (
            <NavItem
              text={t('media360', { ns: 'common' })}
              icon="icon-360"
              onClick={() => scrollInto(controlMenu.threesixtyAndSphere)}
              current={threesixtyAndSphereVisible(controlMenu, visibleIndex)}
            />
          )}
          {controlMenu.embedAndVideo >= 0 && (
            <NavItem
              text={t('mediaVideo', { ns: 'common' })}
              icon="icon-play"
              onClick={() => scrollInto(controlMenu.embedAndVideo)}
              current={embedAndVideoVisible(controlMenu, visibleIndex)}
            />
          )}
        </ul>
      </nav>
    );
  }

  return (
    <div
      data-am-lightbox={modifiers}
      ref={ref}
      onScroll={onPageScroll}
      className={className}
      onKeyDown={onKeyDown}
    >
      <div className="lightbox">
        <div className="lightbox-nav-container">
          <div className="lightbox-nav-upper">
            <div className="lightbox-close-btn-container">
              <button
                type="button"
                className="lightbox-close-btn"
                onClick={onClose}
                aria-label="Stäng bildgalleri"
              >
                <div className="lightbox-close-btn-title">{t('close', { ns: 'common' })}</div>
                <div className="lightbox-close-btn-icon">
                  <i className="icon-close no-margin" />
                </div>
              </button>
            </div>
            {menu}
          </div>
          {(price || privateLeasing || businessLeasing || data?.usps) && (
            <div className="lightbox-nav-lower">
              <hr className="m-b" />
              {price}
              {privateLeasing}
              {businessLeasing}
              <hr className="m-y" />
              {data?.usps?.map((usp) => (
                <div key={usp.value} className="m-b-half">
                  <i className={`${usp.icon} text-action m-r`} />
                  <span className="font-medium">{usp.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="lightbox-media-container" onClick={onClose}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Lightbox;
